import React, { useEffect, useState } from 'react'
import { message } from 'antd'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'

import useAxios, { axiosInstance } from '../../../../../hooks/useAxios'
import { EDistributionChannelType } from '../../../../../types/distribution-channel'
import CircularLoading from '../../../../../components/CircularProgress'
import { IAddress, IAddressThailand } from '../../../../../types/address'
import { IOrder } from '../../../../../types/order'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'
import SelectSearch, {
  IOptionSelect,
} from '../../../../../components/SelectSearch'

interface IOrderAddressFormModalProps {
  isOpen: boolean
  onClose: (visible: boolean) => void
  order?: IOrder
  onUpdateAddressListSuccess: () => void
}

interface IOrderAddressFormModal {
  name: string
  phone: string
  address: string
  subDistrict: string
  district: string
  province: string
  zipcode: string
  email?: string
  social?: string
  crn?: string
  refId?: string
}

const menuItemsType = [
  {
    label: 'Facebook',
    value: EDistributionChannelType.FACEBOOK,
  },
  {
    label: 'Tiktok',
    value: EDistributionChannelType.TIKTOK,
  },
  {
    label: 'Instagram',
    value: EDistributionChannelType.INSTAGRAM,
  },
  {
    label: 'Shopee',
    value: EDistributionChannelType.SHOPEE,
  },
  {
    label: 'Lazada',
    value: EDistributionChannelType.LAZADA,
  },
  {
    label: 'อื่นๆ',
    value: EDistributionChannelType.ETC,
  },
]

const getAddressFormValidationSchema = () => {
  return Yup.object({
    name: Yup.string().required('กรุณากรอกชื่อ-นามสกุล'),
    phone: Yup.string().required('กรุณากรอกหมายเลขโทรศัพท์'),
    address: Yup.string().required('กรุณากรอกที่อยู่'),
    subDistrict: Yup.string().required('กรุณากรอกตำบล'),
    district: Yup.string().required('กรุณากรอกอำเภอ'),
    province: Yup.string().required('กรุณากรอกจังหวัด'),
    zipcode: Yup.string().required('กรุณากรอกรหัสไปรษณีย์'),
    social: Yup.string(),
  })
}

export const OrderAddressFormModal = (props: IOrderAddressFormModalProps) => {
  const { isOpen, onClose, order, onUpdateAddressListSuccess } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchAddress, setSearchAddress] = useState<string>('')
  useEffect(() => {
    setIsLoading(true)
    formik.setValues({
      name: order?.recipient.name || '',
      phone: order?.recipient.phone || '',
      address: order?.recipient.address || '',
      subDistrict: order?.recipient.subDistrict || '',
      district: order?.recipient.district || '',
      province: order?.recipient.province || '',
      zipcode: order?.recipient.zipcode || '',
      email: order?.recipient.email || '',
      social: order?.recipient.social || '',
      crn: order?.recipient.crn || '',
      refId: order?.referrer?.refId || '',
    })
    setIsLoading(false)
  }, [isOpen, order])

  const formik = useFormik<IOrderAddressFormModal>({
    initialValues: {
      name: '',
      phone: '',
      address: '',
      subDistrict: '',
      district: '',
      province: '',
      zipcode: '',
      email: '',
      social: '',
      crn: '',
      refId: '',
    } as IOrderAddressFormModal,
    validationSchema: getAddressFormValidationSchema(),
    onSubmit: async (values: IOrderAddressFormModal) => {
      setIsLoading(true)
      try {
        await axiosInstance.put(`/order/${order?.objectId}/address`, {
          name: values.name,
          phone: values.phone,
          address: values.address,
          subDistrict: values.subDistrict,
          district: values.district,
          province: values.province,
          zipcode: `${values.zipcode}`,
          email: values.email,
          social: values.social,
          crn: values.crn,
          refId: values.refId,
        })
        message.success(`แก้ไขที่อยู่ ${values?.address} สำเร็จ`)
        formik.resetForm()
        onClose(false)
        onUpdateAddressListSuccess()
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  const addressList = useAxios<IAddressThailand[]>({
    method: 'GET',
    url: '/address/thailand-address',
    params: {
      page: 1,
      perPage: 5,
      search: searchAddress,
      startDate: '',
      endDate: '',
    },
  })
  const handleSearchAddress = async (value: string) => {
    setSearchAddress(value)
    await addressList.mutate()
  }

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="md">
      <CircularLoading isLoading={isLoading} />
      <DialogTitle>แก้ไขที่อยู่ผู้รับ</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={12}>
              <TextField
                id="name"
                fullWidth
                label="ชื่อ-นามสกุล"
                name="name"
                helperText={
                  formik.touched.name && (formik.errors.name as string)
                }
                error={!!(formik.touched.name && formik.errors.name)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="phone"
                fullWidth
                label="เบอร์โทรศัพท์"
                name="phone"
                helperText={
                  formik.touched.phone && (formik.errors.phone as string)
                }
                error={!!(formik.touched.phone && formik.errors.phone)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.phone}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="social"
                fullWidth
                label="ช่องทางการติดต่อ"
                name="social"
                helperText={
                  formik.touched.social && (formik.errors.social as string)
                }
                error={!!(formik.touched.social && formik.errors.social)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.social}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="address"
                fullWidth
                label="ที่อยู่"
                name="address"
                helperText={
                  formik.touched.address && (formik.errors.address as string)
                }
                error={!!(formik.touched.address && formik.errors.address)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.address}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <SelectSearch<IAddressThailand>
                label={'ค้นหาที่อยู่'}
                onSelect={(e) => {
                  formik.setFieldValue('subDistrict', e?.value.subDistrict)
                  formik.setFieldValue('district', e?.value.district)
                  formik.setFieldValue('province', e?.value.province)
                  formik.setFieldValue('zipcode', e?.value.zipcode)
                }}
                search={searchAddress}
                onSearch={handleSearchAddress}
                options={
                  addressList.data?.map((address: IAddressThailand) => ({
                    label: `${address.subDistrict} ${address.district} ${address.province} ${address.zipcode}`,
                    value: address,
                  })) || ([] as IOptionSelect<IAddressThailand>[])
                }
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="subDistrict"
                fullWidth
                label="แขวง/ตำบล"
                name="subDistrict"
                helperText={
                  formik.touched.subDistrict &&
                  (formik.errors.subDistrict as string)
                }
                error={
                  !!(formik.touched.subDistrict && formik.errors.subDistrict)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.subDistrict}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="district"
                fullWidth
                label="อำเภอ"
                name="district"
                helperText={
                  formik.touched.district && (formik.errors.district as string)
                }
                error={!!(formik.touched.district && formik.errors.district)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.district}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="province"
                fullWidth
                label="จังหวัด"
                name="province"
                helperText={
                  formik.touched.province && (formik.errors.province as string)
                }
                error={!!(formik.touched.province && formik.errors.province)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.province}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="zipcode"
                fullWidth
                label="รหัสไปรษณีย์"
                name="zipcode"
                helperText={
                  formik.touched.zipcode && (formik.errors.zipcode as string)
                }
                error={!!(formik.touched.zipcode && formik.errors.zipcode)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.zipcode}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="email"
                fullWidth
                label="อีเมล"
                name="email"
                helperText={
                  formik.touched.email && (formik.errors.email as string)
                }
                error={!!(formik.touched.email && formik.errors.email)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.email}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="crn"
                fullWidth
                label="เลขประจำตัวผู้เสียภาษี"
                name="crn"
                helperText={formik.touched.crn && (formik.errors.crn as string)}
                error={!!(formik.touched.crn && formik.errors.crn)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.crn}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="refId"
                fullWidth
                label="หมายเลขใบเสร็จรับเงิน"
                name="refId"
                helperText={
                  formik.touched.refId && (formik.errors.refId as string)
                }
                error={!!(formik.touched.refId && formik.errors.refId)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.refId}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                disabled={formik.isSubmitting}
                startIcon={
                  formik.isSubmitting ? (
                    <CircularProgress size={18} />
                  ) : (
                    <SaveIcon />
                  )
                }
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                แก้ไขที่อยู่ผู้รับ
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
